const FOOTER_MENU_ANIMATION_DURATION = 240;
const FOOTER_MENU_ANIMATION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

class FooterMenuDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.footer-block__details-content');

    if (!this.details || !this.summary || !this.content) return;

    this.mediaQuery = window.matchMedia('(min-width: 750px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.handleViewportChange = this.handleViewportChange.bind(this);
    this.handleSummaryClick = this.handleSummaryClick.bind(this);
    this.handleSummaryKeydown = this.handleSummaryKeydown.bind(this);
    this.syncExpandedState = this.syncExpandedState.bind(this);

    this.summary.addEventListener('click', this.handleSummaryClick);
    this.summary.addEventListener('keydown', this.handleSummaryKeydown);
    this.details.addEventListener('toggle', this.syncExpandedState);

    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleViewportChange);
    } else {
      this.mediaQuery.addListener(this.handleViewportChange);
    }

    this.handleViewportChange(this.mediaQuery);
  }

  disconnectedCallback() {
    if (!this.details || !this.summary || !this.mediaQuery) return;

    this.cancelAnimation();
    this.summary.removeEventListener('click', this.handleSummaryClick);
    this.summary.removeEventListener('keydown', this.handleSummaryKeydown);
    this.details.removeEventListener('toggle', this.syncExpandedState);

    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleViewportChange);
    } else {
      this.mediaQuery.removeListener(this.handleViewportChange);
    }
  }

  handleViewportChange(event) {
    this.setOpenState(event.matches);

    if (event.matches) {
      this.summary.setAttribute('tabindex', '-1');
      this.summary.setAttribute('aria-disabled', 'true');
    } else {
      this.summary.removeAttribute('tabindex');
      this.summary.removeAttribute('aria-disabled');
    }
  }

  handleSummaryClick(event) {
    event.preventDefault();
    if (this.mediaQuery.matches) return;

    this.toggleDisclosure();
  }

  handleSummaryKeydown(event) {
    const isToggleKey = event.key === 'Enter' || event.key === ' ';

    if (!isToggleKey) return;

    event.preventDefault();
    if (this.mediaQuery.matches) return;

    this.toggleDisclosure();
  }

  toggleDisclosure() {
    if (this.isClosing || !this.details.open) {
      this.openDisclosure();
    } else {
      this.closeDisclosure();
    }
  }

  openDisclosure() {
    if (!this.shouldAnimate()) {
      this.setOpenState(true);
      return;
    }

    const startHeight = this.details.getBoundingClientRect().height;

    this.cancelAnimation();
    this.isExpanding = true;
    this.details.classList.add('is-expanding');
    this.details.open = true;
    this.details.style.height = '';

    const endHeight = this.details.getBoundingClientRect().height;

    this.details.style.height = `${startHeight}px`;
    this.details.style.overflow = 'hidden';
    this.syncExpandedState();
    this.animation = this.details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      {
        duration: FOOTER_MENU_ANIMATION_DURATION,
        easing: FOOTER_MENU_ANIMATION_EASING,
        fill: 'both',
      }
    );
    this.animation.onfinish = () => this.finishAnimation(true);
  }

  closeDisclosure() {
    if (!this.shouldAnimate()) {
      this.setOpenState(false);
      return;
    }

    const startHeight = this.details.getBoundingClientRect().height;
    const detailsStyles = window.getComputedStyle(this.details);
    const borderHeight =
      Number.parseFloat(detailsStyles.borderTopWidth) + Number.parseFloat(detailsStyles.borderBottomWidth);
    const endHeight = this.summary.getBoundingClientRect().height + borderHeight;

    this.cancelAnimation();
    this.isClosing = true;
    this.details.classList.add('is-closing');
    this.details.style.height = `${startHeight}px`;
    this.details.style.overflow = 'hidden';
    this.syncExpandedState();
    this.animation = this.details.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      {
        duration: FOOTER_MENU_ANIMATION_DURATION,
        easing: FOOTER_MENU_ANIMATION_EASING,
        fill: 'both',
      }
    );
    this.animation.onfinish = () => this.finishAnimation(false);
  }

  shouldAnimate() {
    return !this.reducedMotionQuery.matches && typeof this.details.animate === 'function';
  }

  setOpenState(isOpen) {
    this.cancelAnimation();
    this.details.open = isOpen;
    this.clearAnimationStyles();
    this.syncExpandedState();
  }

  finishAnimation(isOpen) {
    if (this.animation) this.animation.cancel();

    this.details.open = isOpen;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.classList.remove('is-closing', 'is-expanding');
    this.clearAnimationStyles();
    this.syncExpandedState();
  }

  cancelAnimation() {
    if (this.animation) this.animation.cancel();

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.classList.remove('is-closing', 'is-expanding');
  }

  clearAnimationStyles() {
    this.details.style.height = '';
    this.details.style.overflow = '';
  }

  syncExpandedState() {
    const isExpanded = this.isClosing ? false : this.details.open;
    this.summary.setAttribute('aria-expanded', String(isExpanded));
  }
}

if (!customElements.get('footer-menu-disclosure')) {
  customElements.define('footer-menu-disclosure', FooterMenuDisclosure);
}
